import React from 'react';
import { Box, Button, Fade, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { Computer as ComputerIcon, OpenInNew as OpenInNewIcon, Public as PublicIcon } from '@mui/icons-material';
import type { DroneView } from '../../services/api';

/** Same footprint as the Views slider, so switching modes doesn't jump the card. */
const FRAME_HEIGHT = 'min(600px, 65vh)';

/** After this long without a load event, swap to the reassurance message. */
const LONG_WAIT_MS = 30_000;

const ripple = keyframes`
  0% { transform: scale(0.6); opacity: 0.7; }
  100% { transform: scale(1.8); opacity: 0; }
`;

/** A bit travelling the wire from source to screen. */
const travel = keyframes`
  0% { left: 0%; opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
  15% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  85% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  100% { left: 100%; opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
`;

/** Screen flickers as each bit lands. */
const receive = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(232, 135, 58, 0); }
  50% { box-shadow: 0 0 18px 4px rgba(232, 135, 58, 0.55); }
`;

const dash = keyframes`
  to { background-position: 24px 0; }
`;

const dot = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

/** Covers the iframe until the tour reports loaded. */
function DroneLoadingScreen({ longWait }: { longWait: boolean }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        px: 3,
        textAlign: 'center',
        pointerEvents: 'none',
        background: 'radial-gradient(circle at 50% 40%, #1f2a3a 0%, #0b0f16 70%)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: 280, maxWidth: '100%' }}>
        {/* External source: a globe broadcasting signal rings. */}
        <Box sx={{ position: 'relative', width: 64, height: 64, flexShrink: 0 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '2px solid rgba(232, 135, 58, 0.6)',
                animation: `${ripple} 2.4s ease-out infinite`,
                animationDelay: `${i * 0.8}s`,
              }}
            />
          ))}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.25)',
            }}
          >
            <PublicIcon sx={{ fontSize: 34, color: '#fff' }} />
          </Box>
        </Box>

        {/* The wire, with 1s and 0s streaming across it. */}
        <Box sx={{ position: 'relative', flex: 1, height: 32, mx: 1.5 }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              height: 2,
              mt: '-1px',
              backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0.35) 50%, transparent 50%)',
              backgroundSize: '12px 2px',
              animation: `${dash} 0.8s linear infinite`,
            }}
          />
          {['1', '0', '1', '1', '0'].map((bit, i) => (
            <Box
              key={i}
              component="span"
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                px: 0.5,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.4,
                color: '#0b0f16',
                bgcolor: '#e8873a',
                boxShadow: '0 0 10px 2px rgba(232, 135, 58, 0.7)',
                opacity: 0,
                animation: `${travel} 2s linear infinite`,
                animationDelay: `${i * 0.4}s`,
              }}
            >
              {bit}
            </Box>
          ))}
        </Box>

        {/* Destination: this screen, glowing as each bit lands. */}
        <Box
          sx={{
            width: 64,
            height: 64,
            flexShrink: 0,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.25)',
            animation: `${receive} 0.8s ease-in-out infinite`,
          }}
        >
          <ComputerIcon sx={{ fontSize: 34, color: '#fff' }} />
        </Box>
      </Box>
      <Box sx={{ maxWidth: 420 }}>
        <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>
          {longWait ? 'Still capturing the skyline for you' : 'Loading Drone View From External Source'}
          <Box component="span" sx={{ display: 'inline-flex', ml: 0.25 }}>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                component="span"
                sx={{ animation: `${dot} 1.4s infinite`, animationDelay: `${i * 0.2}s` }}
              >
                .
              </Box>
            ))}
          </Box>
        </Typography>
        <Fade in={longWait} timeout={600}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
            High-resolution aerial imagery takes a little extra time to arrive from the
            tour provider. Hang tight — the view is on its way.
          </Typography>
        </Fade>
      </Box>
    </Box>
  );
}

/** Only https tours are framed. The server enforces this on save as well. */
function safeTourUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * Site-plan "Drone" mode: the aerial / 360° tour an admin attached in Manage
 * Drone View, embedded in a sandboxed iframe. Mounted only while the mode is
 * selected, so the (heavy) tour never loads in the background.
 */
export default function SitePlanDroneView({ droneView }: { droneView: DroneView }) {
  const src = safeTourUrl(droneView.url);
  const [loaded, setLoaded] = React.useState(false);
  const [longWait, setLongWait] = React.useState(false);

  React.useEffect(() => {
    setLoaded(false);
    setLongWait(false);
    const timer = window.setTimeout(() => setLongWait(true), LONG_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [src]);

  if (!src) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
        This drone view link is not valid.
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          height: FRAME_HEIGHT,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: '#000',
        }}
      >
        <Fade in={!loaded} timeout={{ enter: 0, exit: 500 }} unmountOnExit>
          <Box>
            <DroneLoadingScreen longWait={longWait} />
          </Box>
        </Fade>
        <iframe
          key={src}
          src={src}
          title={droneView.label || 'Drone view'}
          onLoad={() => setLoaded(true)}
          allow="fullscreen; gyroscope; accelerometer; xr-spatial-tracking"
          allowFullScreen
          // Tours need scripts and their own origin (krpano loads its XML over
          // XHR); top-level navigation stays blocked.
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }} noWrap>
          {droneView.label || 'Drag to look around · Pinch or scroll to zoom'}
        </Typography>
        {/* Fallback for shells where fullscreen inside an iframe is unavailable. */}
        <Button
          size="small"
          startIcon={<OpenInNewIcon fontSize="small" />}
          onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
          sx={{ flexShrink: 0 }}
        >
          Open full screen
        </Button>
      </Box>
    </Box>
  );
}
